/* @jsx jsx */
import React from 'react';
import { useIntl } from 'react-intl-next';
import { bind } from 'bind-event-listener';
import { jsx } from '@emotion/react';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { LoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';
import { getPMFragmentWithFallback } from '../get-pm-fragment-with-fallback';
import { useAnalyticsFlowEventsQueue } from '../../analytics/analytics-flow/analyticsFlowUtils';
import { convertPMFragmentToPreview } from '../../ui/convert-markdown-to-preview';
import { scrollTopMargin, scrollMarginStyles } from '@atlassian/generative-ai-modal/styles/FloatingContainer';
import { useFireAIAnalyticsEvent } from '../../analytics/utils';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { isMarkdownEndingWithCustomTag } from '@atlassian/ai-model-io/utils/is-markdown-with-custom-tag';

// Selectors will be used for editorView.dom.closest() to track
// the scroll parent element and scrollable content height
const AK_EDITOR_CONTENT_AREA_SELECTOR = '.ak-editor-content-area';
export function LoadingScreenWithLogic({
  providerFactory,
  title,
  latestPrompt,
  promptTrigger,
  latestPromptRetried,
  markdown,
  schema,
  editorView,
  send,
  idMap,
  contextStatistics
}) {
  const {
    formatMessage
  } = useIntl();
  const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
  const [preview, setPreview] = React.useState();
  const [hasPreview, setHasPreview] = React.useState(false);
  const containerRef = React.useRef(null);

  // raf is used for debouncing, if useEffect is called
  // multiple times before raf is run this prevents rendering
  // multiple times if we receive streams very quickly
  const raf = React.useRef();
  const errorReported = React.useRef(false);

  /**
   * scrollParentRef is for
   * storing the scroll parent element once its found
   * to be re-used in calculations
   */
  const scrollParentRef = React.useRef(null);
  const initialScrollTop = React.useRef(0);
  const modalOffsetTop = React.useRef(0);
  const isUserScroll = React.useRef(false);
  /**
   * isProgrammaticScroll is set to true initially
   * to detect any initial changes on render which affect
   * scroll position. There will be multiple scroll events
   * within a single user scroll so it will be set to false
   * accordingly within a single scroll action.
   */
  const isProgrammaticScroll = React.useRef(true);
  const isModalAtTop = React.useRef(false);
  const scrollModalAlignBottom = React.useCallback((modalEl, scrollParentEl) => {
    // We set isProgrammaticScroll to true to flag it as a non-user scroll.
    isProgrammaticScroll.current = true;
    scrollParentEl.scroll({
      top: initialScrollTop.current + modalOffsetTop.current + modalEl.scrollHeight - scrollParentEl.offsetHeight,
      // Ignore is for typecheck failing unexpectedly on pipeline
      // @ts-ignore-next-line
      behavior: 'instant'
    });
  }, []);
  const scrollModalToTop = React.useCallback(scrollParentEl => {
    // We set isProgrammaticScroll to true to flag it as a non-user scroll.
    isProgrammaticScroll.current = true;
    scrollParentEl.scroll({
      top: initialScrollTop.current + modalOffsetTop.current - scrollTopMargin,
      // Ignore is for typecheck failing unexpectedly on pipeline
      // @ts-ignore-next-line
      behavior: 'instant'
    });
  }, []);
  React.useEffect(() => {
    /**
     * Set up ResizeObserver to detect cases where the editor content grows,
     * as it will normally trigger a scroll event due to scrollHeight and scrollTop changes.
     * We set isProgrammaticScroll to true to flag it as a non-user scroll.
     */
    const editorContentArea = editorView.dom.closest(AK_EDITOR_CONTENT_AREA_SELECTOR);
    if (!editorContentArea) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      isProgrammaticScroll.current = true;
    });
    resizeObserver.observe(editorContentArea);
    return () => resizeObserver.unobserve(editorContentArea);
  }, [editorView]);
  React.useEffect(() => {
    /**
     * Setup init values to calculate scroll distance and
     * scroll the modal into view if it is not already visible.
     */
    const modalEl = containerRef.current;
    if (!modalEl) {
      return;
    }
    const scrollParentEl = findOverflowScrollParent(modalEl);
    if (!scrollParentEl) {
      return;
    }
    scrollParentRef.current = scrollParentEl;

    // Calculate the offset of the modal from the top of editor
    modalOffsetTop.current = modalEl.getBoundingClientRect().top - scrollParentEl.getBoundingClientRect().top;

    // Calculate initial scroll position of editor
    initialScrollTop.current = scrollParentEl.scrollTop;

    // Calculate position of the bottom of the modal from top of editor
    const modalBottomOffsetTop = modalOffsetTop.current + modalEl.scrollHeight;
    if (modalBottomOffsetTop < 0) {
      // Edge case: if modal above the top of the editor
      scrollModalToTop(scrollParentEl);
    } else if (modalOffsetTop.current > scrollParentEl.offsetHeight) {
      /**
       * Edge case: if modal is below the bottom of the editor
       * Call scrollModalAlignBottom to move modal into view.
       */
      scrollModalAlignBottom(modalEl, scrollParentEl);
    }
    const unbind = bind(scrollParentEl, {
      type: 'scroll',
      listener: () => {
        if (!isProgrammaticScroll.current) {
          isUserScroll.current = true;
          unbind();
        }
        isProgrammaticScroll.current = false;
      }
    });
    return unbind;
  }, [scrollModalToTop, scrollModalAlignBottom]);
  function scrollModalIntoView() {
    /**
     * Early exits:
     * - If user has manually scrolled, stop auto-scrolling
     * - If modal has reached the top of the editor, no more calculations or scrolling needed
     */
    if (isUserScroll.current || isModalAtTop.current) {
      return;
    }
    const modalEl = containerRef.current;
    if (!modalEl) {
      return;
    }
    const scrollParentEl = scrollParentRef.current;
    if (!scrollParentEl) {
      return;
    }

    // Check if modal is already fully visible
    const isModalFullyVisible = scrollParentEl.offsetHeight > modalOffsetTop.current + modalEl.scrollHeight;

    /**
     * Check if modal height exceeds scroll parent's height
     * Scroll margin is used to prevent the modal from being scrolled to the top
     * and should match scroll margin in other modals e.g. from
     * packages/editor/editor-plugin-ai/src/ui/components/FloatingContainer/styles.ts
     */
    const isModalFullHeight = scrollParentEl.offsetHeight - scrollTopMargin < modalEl.scrollHeight;
    if (isModalFullyVisible) {
      return;
    }
    if (isModalFullHeight) {
      scrollModalToTop(scrollParentEl);
      isModalAtTop.current = true;
      return;
    }
    scrollModalAlignBottom(modalEl, scrollParentEl);
  }
  React.useEffect(() => {
    // only run the callback if the raf has not been set, and then
    // reset raf to undefined
    if (raf.current === undefined) {
      raf.current = window.requestAnimationFrame(() => {
        let _preview;
        raf.current = undefined;
        if (!markdown || markdown === '') {
          // When there is no streamed preview content -- set the preview to
          // undefined.
          // This allows a nicer loading screen (rather than showing an empty paragraph).
          _preview = undefined;
        } else {
          const {
            pmFragment
          } = getPMFragmentWithFallback({
            markdown: markdown,
            schema,
            source: 'loading',
            fireAIAnalyticsEvent: event => {
              if (!errorReported.current) {
                errorReported.current = true;
                fireAIAnalyticsEvent(event);
              }
            },
            idMap: idMap
          });

          /**
           * Hold off on setting the preview if we appear to be receiving
           * a custom tag through our stream. This is to hide raw tags from
           * users until the tags are complete and can be rendered back into nodes.
           */
          if (getBooleanFF('platform.editor.ai-markdown-plus_9v1w7') && isMarkdownEndingWithCustomTag(markdown,
          // eslint-disable-next-line @atlaskit/platform/no-invalid-feature-flag-usage
          getBooleanFF('platform.editor.ai-markdown-plus-v2-streaming_o9h6u'))) {
            return;
          }
          _preview = convertPMFragmentToPreview({
            schema,
            pmFragment,
            showTelepointer: true,
            providerFactory
          });
          setPreview(_preview);
          setHasPreview(true);
          scrollModalIntoView();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown]);
  React.useEffect(() => {
    return () => {
      // if the component is unmounted before the raf callback
      // has run, we cancel it
      if (raf.current) {
        window.cancelAnimationFrame(raf.current);
      }
    };
  }, []);
  const analyticsFlow = useAnalyticsFlowEventsQueue({
    stepName: 'loading',
    attributes: {
      promptType: promptTrigger,
      latestPromptRetried,
      modelIO: {
        contextStatistics
      }
    }
  });
  const previewBecameAvailable = React.useRef();
  if (!previewBecameAvailable.current && hasPreview) {
    previewBecameAvailable.current = performance.now();
  }
  React.useEffect(() => {
    const noPreviewAvailable = performance.now();
    return () => {
      if (!previewBecameAvailable.current) {
        return;
      }
      analyticsFlow.addAttributes({
        durations: {
          timeSpentWithoutPartialPreview: previewBecameAvailable.current - noPreviewAvailable,
          timeSpentWithPartialPreview: performance.now() - previewBecameAvailable.current
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // preset prompt or base generate / interrogation
  const localizedLatestPrompt = latestPrompt !== null && latestPrompt !== void 0 && latestPrompt.presetTitle ? {
    presetTitle: latestPrompt.presetTitle({
      formatMessage
    }),
    userInput: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput,
    userADFInput: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput
  } : {
    userInput: (latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput) || '',
    userADFInput: (latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput) || ''
  };
  return jsx("div", {
    ref: containerRef,
    css: scrollMarginStyles
  }, jsx(LoadingScreen, {
    latestPrompt: localizedLatestPrompt,
    title: title,
    preview: preview,
    providerFactory: providerFactory,
    onCancel: () => {
      send({
        type: 'enable discard'
      });
    },
    schema: schema
  }));
}